'use client';

import { useEffect, useRef, useState } from 'react';
import { useTapestryStagger } from '../lib/tapestryStagger';
import { useSeenReveal } from '../lib/seenReveal';
import { useTapestrySkin } from '../lib/tapestrySkin';
import {
  LINE_DURATION_MS,
  PEN_SIZE_SCALE,
  DUST_SPAWN_INTERVAL_MS,
  DUST_LIFETIME_MS,
  spawnDustParticle,
  advanceParticles,
  penRockOffset,
} from '../lib/penReveal';
import { PenFilter, DustParticles, PenSprite } from './PenDust';

const FONT_FAMILY = 'Hand1-Regular';
const DUST_FILTER_ID = 'tapestry-dust-filter';
const PEN_SPRITE_FILTER_ID = 'tapestry-pen-sprite-filter';

/**
 * Renders the generative external textpaths from precomputed placements.
 *
 * The packer (tapestryPacker.js) has already measured the text and decided
 * the curve shape, font size, and per-line word distribution. This component
 * renders the SVG paths and text declaratively so position changes animate
 * via CSS transform transitions.
 *
 * Each curve reveals with a pen-tracing animation distinct from the symbol
 * reveal: the pen walks the actual bezier curve (line by line for multi-
 * line text), words progressively fade in as the pen passes over them,
 * and a particle system spawns ephemeral chalk puffs at the pen's current
 * position that stay put and dissipate in place.
 *
 * Coordinates in placements are height-normalized; multiply by canvasHeight
 * to get pixels.
 */
export default function TapestryExternal({ placements, canvasWidth, canvasHeight, currentSent }) {
  const skin = useTapestrySkin();
  return (
    <svg
      width={canvasWidth}
      height={canvasHeight}
      viewBox={`0 0 ${canvasWidth} ${canvasHeight}`}
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        // 'screen' makes the white chalk text/pen pop on the dark background;
        // ink on paper blends normally (the skin decides — TT-1133).
        mixBlendMode: skin.blend,
      }}
    >
      <defs>
        <PenFilter id={DUST_FILTER_ID} />
        <PenFilter id={PEN_SPRITE_FILTER_ID} outline />
      </defs>
      {placements.map((p) =>
        p.curve && p.layout ? (
          <ExternalCurve
            // Key on the sentence so a genuine content change remounts and
            // replays the pen-trace; reshapes/slides (same sentence) keep the
            // same key and glide without redrawing.
            key={`${p.symbolId}:${p.sentence ?? ''}`}
            placement={p}
            canvasHeight={canvasHeight}
            currentSent={currentSent}
          />
        ) : null
      )}
    </svg>
  );
}

/**
 * A single external curve + its multi-line text, with pen-tracing reveal.
 *
 * The path d is rendered relative to the curve's start point so the
 * wrapping <g>'s transform: translate(sx, sy) moves the whole block as
 * one unit, both during layout-slides and during the reveal animation.
 *
 * Reveal is driven by a requestAnimationFrame loop that updates a
 * `progress` state in [0, 1]. Progress is split equally across lines:
 * when progress is in [i/n, (i+1)/n), the pen is on line i at normalized
 * position (progress*n - i). Words on earlier lines are fully visible,
 * words on later lines are hidden, and words on the current line are
 * revealed past (wordIndex + 0.5) / wordCount.
 */
function ExternalCurve({ placement, canvasHeight, currentSent }) {
  const skin = useTapestrySkin();
  // Track per-line birth frame keyed by content. <text> elements are keyed
  // by lineIdx and reused across content changes, so we can't rely on
  // mount/unmount alone — we record the frame at which each line's content
  // last changed and compare against currentSent to decide fresh vs. aged.
  const lineBirthRef = useRef({ keys: [], sents: [] });
  const pathRef = useRef(null);
  const textPathRefs = useRef([]);
  // Monotonic particle id counter. Stored in a ref instead of a local
  // inside the effect so React strict-mode's dev-only double-mount
  // doesn't reset it back to 0 and collide with particles still in
  // state from the first mount.
  const nextParticleIdRef = useRef(0);

  // TT-961: consult the scene registry once at mount. `playReveal` is false when
  // this sentence has already been introduced elsewhere/earlier — the curve then
  // renders straight to its final state with no pen-trace and no dust, no matter
  // where it moved from. New text (or the same text replayed at its own intro
  // frame after a scrub-back) keeps the full reveal.
  const seen = useSeenReveal();
  const playRef = useRef(null);
  if (playRef.current === null) {
    playRef.current = seen ? seen.reveal(placement.sentence) : true;
  }
  const playReveal = playRef.current && !seen?.static;

  // progress + dust particles live in a single state object so React
  // batches them into one render per frame.
  const [animState, setAnimState] = useState({ progress: 0, particles: [] });
  const { progress, particles } = animState;

  // True once the stagger queue says it's our turn to draw. Text/pen/dust
  // are gated on this so the curve is invisible while queued. Already-seen text
  // skips the queue entirely and shows immediately.
  const [started, setStarted] = useState(!playReveal);
  const stagger = useTapestryStagger();
  const reservationRef = useRef(null);

  // TT-961 within-phase transition: seen text that remounts here (e.g. a curve
  // whose sentence changed to one already shown elsewhere) fades in rather than
  // popping — crossfading against whatever it replaced. New text starts opaque
  // and reveals via the pen intro instead.
  const [appeared, setAppeared] = useState(playReveal);
  useEffect(() => {
    if (appeared) return;
    const id = requestAnimationFrame(() => setAppeared(true));
    return () => cancelAnimationFrame(id);
  }, [appeared]);

  const p = placement;
  const sx = p.curve.start.x * canvasHeight;
  const sy = p.curve.start.y * canvasHeight;
  const ex = p.curve.end.x * canvasHeight;
  const ey = p.curve.end.y * canvasHeight;
  const cxAbs = p.curve.ctrl.x * canvasHeight;
  const cyAbs = p.curve.ctrl.y * canvasHeight;

  // Path d relative to (sx, sy) so the <g> transform can move the whole
  // curve without rewriting path geometry.
  const p0x = 0,
    p0y = 0;
  const p1x = cxAbs - sx,
    p1y = cyAbs - sy;
  const p2x = ex - sx,
    p2y = ey - sy;
  const dRel = `M ${p0x} ${p0y} Q ${p1x} ${p1y} ${p2x} ${p2y}`;
  const pathId = `ext-${p.symbolId}`;
  const lineHeight = p.layout.lineHeightPx;
  const lineCount = p.layout.lineCount;
  const totalDuration = Math.max(1, lineCount) * LINE_DURATION_MS;
  const penSize = lineHeight * PEN_SIZE_SCALE;

  // rAF-driven reveal. Runs once per mount. A real sentence change remounts the
  // curve (keyed on the sentence text upstream) so the pen-trace replays; an
  // in-place reshape that keeps the same sentence (role/lineCount change, layout
  // slide) does *not* remount and snaps in without replaying. The loop advances
  // progress, spawns/ages dust particles, and runs for totalDuration +
  // DUST_LIFETIME_MS so lingering particles can fade out cleanly.
  //
  // The `step` closure captures the curve geometry (lineCount, lineHeight,
  // p0x..p2y, totalDuration) at mount time. If the placement reshapes
  // mid-life, dust particles spawn against stale geometry — visually
  // hidden inside the current `<g transform>`, but technically off the
  // current curve. Acceptable since reshapes are usually small.
  useEffect(() => {
    // Already-introduced text doesn't replay the pen-trace (TT-961): no queue
    // slot, no rAF loop — it's already shown at its final state.
    if (!playReveal) return;
    let raf = null;
    let startTime = null;
    let lastSpawnTime = 0;
    let cancelled = false;
    const extendedDuration = totalDuration + DUST_LIFETIME_MS;

    const step = (time) => {
      if (cancelled) return;
      if (startTime == null) {
        startTime = time;
        lastSpawnTime = time - DUST_SPAWN_INTERVAL_MS; // allow first spawn immediately
      }
      const elapsed = time - startTime;
      const t = Math.min(1, elapsed / totalDuration);

      // Compute pen position at current t for particle spawning
      const scaled = t * lineCount;
      const curLine = Math.min(lineCount - 1, Math.floor(scaled));
      const lt = Math.min(1, scaled - curLine);
      const omt = 1 - lt;
      const pxOnPath = omt * omt * p0x + 2 * omt * lt * p1x + lt * lt * p2x;
      const pyOnPath = omt * omt * p0y + 2 * omt * lt * p1y + lt * lt * p2y;
      const spawnX = pxOnPath;
      const spawnY = pyOnPath + curLine * lineHeight;

      // Probabilistically spawn a new particle while the pen is still writing
      let newParticle = null;
      if (t < 1 && time - lastSpawnTime >= DUST_SPAWN_INTERVAL_MS * (0.7 + Math.random() * 0.9)) {
        lastSpawnTime = time;
        newParticle = spawnDustParticle(nextParticleIdRef.current++, time, spawnX, spawnY, lineHeight);
      }

      setAnimState((prev) => ({ progress: t, particles: advanceParticles(prev.particles, time, newParticle) }));

      if (elapsed < extendedDuration) {
        raf = requestAnimationFrame(step);
      }
    };

    const reservation = stagger?.schedule(totalDuration, () => {
      if (cancelled) return;
      setStarted(true);
      raf = requestAnimationFrame(step);
    });
    reservationRef.current = reservation ?? null;
    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
      reservation?.cancel();
      reservationRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If the curve gets removed before its turn, release the slot now so
  // the queue doesn't burn time on an invisible draw.
  useEffect(() => {
    if (p.exiting && reservationRef.current) {
      reservationRef.current.cancel();
      reservationRef.current = null;
    }
  }, [p.exiting]);

  // Per-line lengthAdjust safety net (unchanged from previous version).
  useEffect(() => {
    const pathEl = pathRef.current;
    if (!pathEl) return;
    let pathLen = 0;
    try {
      pathLen = pathEl.getTotalLength();
    } catch {
      return;
    }
    if (!pathLen) return;
    for (const tp of textPathRefs.current) {
      if (!tp) continue;
      try {
        const measured = tp.getComputedTextLength();
        if (measured > pathLen) {
          tp.setAttribute('textLength', pathLen);
          tp.setAttribute('lengthAdjust', 'spacing');
        } else {
          tp.removeAttribute('textLength');
          tp.removeAttribute('lengthAdjust');
        }
      } catch {
        // not laid out yet; skip
      }
    }
  });

  // Already-seen text is fully revealed with no pen (progress pinned to 1);
  // new text follows the rAF-driven progress.
  const revealProgress = playReveal ? progress : 1;

  // Derive which line the pen is currently on, and its within-line position.
  const scaled = revealProgress * lineCount;
  const currentLine = Math.min(lineCount - 1, Math.floor(scaled));
  const lineT = Math.min(1, scaled - currentLine);

  // Bezier point + tangent at parameter lineT (quadratic curve)
  const oneMinusT = 1 - lineT;
  const penXOnPath = oneMinusT * oneMinusT * p0x + 2 * oneMinusT * lineT * p1x + lineT * lineT * p2x;
  const penYOnPath = oneMinusT * oneMinusT * p0y + 2 * oneMinusT * lineT * p1y + lineT * lineT * p2y;
  const penX = penXOnPath;
  const penY = penYOnPath + currentLine * lineHeight;
  // Tangent direction for pen rotation
  const tanX = 2 * oneMinusT * (p1x - p0x) + 2 * lineT * (p2x - p1x);
  const tanY = 2 * oneMinusT * (p1y - p0y) + 2 * lineT * (p2y - p1y);
  const penAngleDeg = (Math.atan2(tanY, tanX) * 180) / Math.PI + penRockOffset(scaled);
  // Gate on progress > 0 so the pen doesn't briefly appear stationary at
  // the curve start in the frame between `started` flipping true and the
  // rAF's first tick. Never show the pen for already-seen text.
  const penVisible = playReveal && progress > 0 && progress < 1;

  return (
    <g
      className="tapestry-external-curve"
      style={{ transform: `translate(${sx}px, ${sy}px)`, opacity: p.exiting ? 0 : seen?.static || appeared ? 1 : 0 }}
    >
      <path ref={pathRef} id={pathId} d={dRel} fill="none" stroke="none" />

      {(seen?.static || started) &&
        p.layout.lines.map((words, lineIdx) => {
          const contentKey = words.join(' ');
          const prevKey = lineBirthRef.current.keys[lineIdx];
          if (prevKey !== contentKey) {
            lineBirthRef.current.keys[lineIdx] = contentKey;
            lineBirthRef.current.sents[lineIdx] = currentSent;
          }
          // Seen text never flashes chalk-yellow — it renders in its aged
          // (final) color immediately.
          const aged = !playReveal || currentSent !== lineBirthRef.current.sents[lineIdx];
          return (
            <text
              key={lineIdx}
              className={aged ? 'tapestry-text-aged-svg' : 'tapestry-text-new-svg'}
              fontSize={`${p.fontSize}px`}
              fontFamily={skin.font || FONT_FAMILY}
              textAnchor="middle"
              transform={lineIdx > 0 ? `translate(0, ${lineIdx * lineHeight})` : undefined}
            >
              <textPath
                ref={(el) => {
                  textPathRefs.current[lineIdx] = el;
                }}
                href={`#${pathId}`}
                startOffset="50%"
              >
                {words.map((word, wi) => {
                  const fullyRevealed = lineIdx < currentLine;
                  const notYet = lineIdx > currentLine;
                  const wordThreshold = (wi + 0.5) / words.length;
                  const visible = fullyRevealed || (!notYet && lineT >= wordThreshold);
                  return (
                    <tspan key={wi} opacity={visible ? 1 : 0}>
                      {word}
                      {wi < words.length - 1 ? ' ' : ''}
                    </tspan>
                  );
                })}
              </textPath>
            </text>
          );
        })}

      {started && skin.chalk && <DustParticles particles={particles} filterId={DUST_FILTER_ID} />}

      {started && penVisible && (
        <PenSprite x={penX} y={penY} angle={penAngleDeg} size={penSize} filterId={PEN_SPRITE_FILTER_ID} />
      )}
    </g>
  );
}
